/**
 * Role-based navigation configuration
 * Centralized configuration for all navigation elements
 */

import { Role } from '@lov/shared';

// Role accent colors for subtle visual differentiation
export const roleColors = {
  [Role.CUSTOMER]: '#22C55E', // Green - consumer-friendly
  [Role.VENDOR]: '#6B8AFB', // Blue - professional
  [Role.VENDOR_STAFF]: '#8BA4FC', // Light blue - staff
  [Role.SUPER_ADMIN]: '#A78BFA', // Purple - admin
} as const;

// Bottom navigation items for customers
export interface BottomNavItem {
  label: string;
  path: string;
  iconOutlined: string;
  iconFilled: string;
}

export const vendorBottomNavItems: BottomNavItem[] = [
  {
    label: 'Dashboard',
    path: '/vendor/dashboard',
    iconOutlined: 'HomeOutlined',
    iconFilled: 'Home',
  },
  {
    label: 'Bookings',
    path: '/vendor/bookings',
    iconOutlined: 'EventNoteOutlined',
    iconFilled: 'EventNote',
  },
  {
    label: 'Customers',
    path: '/vendor/customers',
    iconOutlined: 'PeopleOutlined',
    iconFilled: 'People',
  },
  {
    label: 'More',
    path: '__drawer__',
    iconOutlined: 'MenuOutlined',
    iconFilled: 'Menu',
  },
];

export const customerBottomNavItems: BottomNavItem[] = [
  {
    label: 'Home',
    path: '/dashboard',
    iconOutlined: 'HomeOutlined',
    iconFilled: 'Home',
  },
  {
    label: 'Venues',
    path: '/venues',
    iconOutlined: 'StoreOutlined',
    iconFilled: 'Store',
  },
  {
    label: 'Bookings',
    path: '/bookings',
    iconOutlined: 'CalendarTodayOutlined',
    iconFilled: 'CalendarToday',
  },
  {
    label: 'Loyalty',
    path: '/loyalty',
    iconOutlined: 'EmojiEventsOutlined',
    iconFilled: 'EmojiEvents',
  },
  {
    label: 'Profile',
    path: '/profile',
    iconOutlined: 'PersonOutlined',
    iconFilled: 'Person',
  },
];

// Avatar dropdown menu items
export interface AvatarMenuItem {
  label: string;
  path?: string;
  icon: string;
  action?: 'logout';
  dividerBefore?: boolean;
  dividerAfter?: boolean;
  roles?: Role[];
}

export const avatarMenuItems: AvatarMenuItem[] = [
  { label: 'Profile', path: '/profile', icon: 'Person' },
  { label: 'Bookings', path: '/bookings', icon: 'CalendarToday', roles: [Role.CUSTOMER] },
  { label: 'Invoices', path: '/invoices', icon: 'Receipt', roles: [Role.CUSTOMER] },
  { label: 'Loyalty', path: '/loyalty', icon: 'EmojiEvents', roles: [Role.CUSTOMER] },
  { label: 'Logout', icon: 'Logout', action: 'logout', dividerBefore: true },
];

// Customer navbar actions (desktop only)
export interface NavbarAction {
  label: string;
  path: string;
  variant: 'text' | 'primary';
  icon?: string;
  roles?: Role[];
}

export const customerNavbarActions: NavbarAction[] = [
  {
    label: 'Register Your Venue',
    path: '/list-your-venue',
    variant: 'text',
    icon: 'AddBusiness',
    roles: [Role.CUSTOMER],
  },
];

// Get role color for avatar ring
export function getRoleColor(role: Role | undefined): string {
  if (!role) return roleColors[Role.CUSTOMER];
  return roleColors[role] || roleColors[Role.CUSTOMER];
}

// Check if user should see customer layout (no sidebar)
export function shouldUseCustomerLayout(role: Role | undefined): boolean {
  return role === Role.CUSTOMER;
}

// Check if user should see bottom navigation on mobile
export function shouldShowBottomNav(role: Role | undefined): boolean {
  return role === Role.CUSTOMER;
}
